/**
 * Страница «Элементы» — основная рабочая область платформы.
 *
 * Не шестнадцать разделов, а один список с фильтрами, сохранёнными
 * представлениями и четырьмя способами смотреть на одни и те же данные:
 * таблица, доска, календарь, список (документ «Структура интерфейса», §2.4).
 *
 * Всё состояние — представление, фильтры, сортировка, открытая карточка —
 * живёт в адресной строке: ссылку можно переслать.
 */

import { InboxIcon, Loader2Icon, PlusIcon } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router';
import type { ItemType } from '@project-os/contracts';
import { useSession } from '@/api/auth';
import type { ItemQuery } from '@/api/items';
import { useItems } from '@/api/items';
import { useProjectMembers } from '@/api/members';
import { useWorkstreams } from '@/api/workstreams';
import { EmptyState, ErrorState, TableSkeleton } from '@/components/common/states';
import { PageHeader } from '@/components/layout/PageHeader';
import { Button } from '@/components/ui/button';
import { pluralRu } from '@/lib/format';
import { rememberRecentItem, useLocalSetting } from '@/lib/use-local-setting';
import { useDisplaySettings } from '@/app/display-settings';
import { useProjectContext } from '../project/use-project-context';
import { ItemDetailPanel } from './ItemDetailPanel';
import { ItemsTable } from './ItemsTable';
import { ItemsToolbar, type ViewMode } from './ItemsToolbar';
import { QuickAddDialog } from './QuickAddDialog';
import { findSavedView, SAVED_VIEWS } from './saved-views';
import { ItemsBoard, type BoardGrouping } from './views/ItemsBoard';
import { ItemsCalendar } from './views/ItemsCalendar';
import { ItemsList } from './views/ItemsList';

/** Задержка перед запросом поиска: набор текста не должен слать запрос на букву. */
const SEARCH_DEBOUNCE_MS = 300;

/** Доска и календарь показывают всё сразу — постраничность им мешает. */
const WIDE_VIEW_LIMIT = 200;

const VIEW_MODES: ViewMode[] = ['table', 'board', 'calendar', 'list'];

export function ItemsPage() {
  const project = useProjectContext();
  const { data: session } = useSession();
  const [searchParams, setSearchParams] = useSearchParams();

  const { data: workstreams = [] } = useWorkstreams(project.id);
  const { data: members = [] } = useProjectMembers(project.id);

  const [quickAddOpen, setQuickAddOpen] = useState(false);
  const [searchDraft, setSearchDraft] = useState(searchParams.get('search') ?? '');
  const [search, setSearch] = useState(searchDraft);

  const viewId = searchParams.get('view') ?? 'all';
  const selectedId = searchParams.get('item');

  // Представление живёт в адресной строке — ссылкой можно поделиться —
  // и одновременно запоминается: человек, работающий на доске, открывает
  // раздел на доске и завтра.
  const [savedMode, setSavedMode] = useLocalSetting<ViewMode>('items-mode', 'table');

  // Плотность — общая настройка отображения, а не свойство одной страницы:
  // два независимых «компактных режима» противоречили бы друг другу.
  const display = useDisplaySettings();
  const density = display.density === 'compact' ? 'compact' : 'normal';

  const modeParam = searchParams.get('mode') as ViewMode | null;
  const mode: ViewMode =
    modeParam && VIEW_MODES.includes(modeParam)
      ? modeParam
      : VIEW_MODES.includes(savedMode)
        ? savedMode
        : 'table';
  const grouping = (searchParams.get('group') as BoardGrouping | null) ?? 'workstream';

  useEffect(() => {
    const timer = setTimeout(() => setSearch(searchDraft), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchDraft]);

  // Фильтры собираются из представления, личного контекста и адресной строки.
  const query = useMemo<ItemQuery>(() => {
    const view = findSavedView(viewId) ?? SAVED_VIEWS[0]!;
    const fromParams = (key: string): string[] | undefined => {
      const value = searchParams.get(key);
      return value ? value.split(',').filter(Boolean) : undefined;
    };

    return {
      ...view.query,
      ...(view.personal && session ? { ownerId: [session.user.id] } : {}),
      ...(fromParams('type') ? { type: fromParams('type') } : {}),
      ...(fromParams('status') ? { status: fromParams('status') } : {}),
      ...(fromParams('workstreamId') ? { workstreamId: fromParams('workstreamId') } : {}),
      ...(fromParams('ownerId') ? { ownerId: fromParams('ownerId') } : {}),
      ...(fromParams('priority') ? { priority: fromParams('priority') } : {}),
      ...(searchParams.get('archived') === 'true' ? { includeArchived: true } : {}),
      sort: searchParams.get('sort') ?? view.query.sort ?? 'updatedAt',
      direction: (searchParams.get('direction') as 'asc' | 'desc') ?? 'desc',
      ...(search ? { search } : {}),
      ...(mode === 'board' || mode === 'calendar' ? { limit: WIDE_VIEW_LIMIT } : {}),
    };
  }, [viewId, searchParams, session, search, mode]);

  const items = useItems(project.id, query);

  const pages = items.data?.pages ?? [];
  const rows = pages.flatMap((page) => page.data);
  const totalCount = pages[0]?.page.totalCount ?? 0;
  const typeCounts = pages[0]?.typeCounts ?? {};

  // Колонки по статусу осмысленны только для одного типа: у каждого типа
  // свой набор статусов.
  const singleType: ItemType | null = query.type?.length === 1 ? (query.type[0] as ItemType) : null;

  const updateParams = useCallback(
    (patch: Record<string, string | null>) => {
      setSearchParams(
        (previous) => {
          const next = new URLSearchParams(previous);
          for (const [key, value] of Object.entries(patch)) {
            if (value === null || value === '') next.delete(key);
            else next.set(key, value);
          }
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  const handleQueryChange = (next: ItemQuery) => {
    updateParams({
      type: next.type?.length ? next.type.join(',') : null,
      status: next.status?.length ? next.status.join(',') : null,
      workstreamId: next.workstreamId?.length ? next.workstreamId.join(',') : null,
      ownerId: next.ownerId?.length ? next.ownerId.join(',') : null,
      priority: next.priority?.length ? next.priority.join(',') : null,
      archived: next.includeArchived ? 'true' : null,
    });
  };

  const handleViewChange = (nextView: string) => {
    // Смена представления сбрасывает ручные фильтры, но не способ показа:
    // выбранная доска или календарь остаются.
    setSearchParams(
      (previous) => {
        const next = new URLSearchParams();
        for (const key of ['item', 'mode', 'group']) {
          const value = previous.get(key);
          if (value) next.set(key, value);
        }
        if (nextView !== 'all') next.set('view', nextView);
        return next;
      },
      { replace: true },
    );
    setSearchDraft('');
  };

  const handleSortChange = (field: string) => {
    const direction = query.sort === field && query.direction === 'desc' ? 'asc' : 'desc';
    updateParams({ sort: field, direction });
  };

  const canCreate = project.permissions.includes('ITEM_WRITE');
  const activeView = findSavedView(viewId) ?? SAVED_VIEWS[0]!;
  const openItem = (itemId: string) => {
    const row = rows.find((entry) => entry.id === itemId);
    if (row) {
      rememberRecentItem({
        id: row.id,
        projectId: project.id,
        title: row.title,
        type: row.type,
      });
    }
    updateParams({ item: itemId });
  };

  // Доска и календарь занимают всю высоту и прокручиваются внутри себя.
  const isWideView = mode === 'board' || mode === 'calendar';

  return (
    <div className={isWideView ? 'flex min-h-0 flex-1 flex-col' : undefined}>
      <PageHeader
        title="Элементы"
        description={
          items.isPending
            ? 'Загрузка…'
            : `${pluralRu(totalCount, ['элемент', 'элемента', 'элементов'])} · ${activeView.description.toLowerCase()}`
        }
      />

      <ItemsToolbar
        query={query}
        onQueryChange={handleQueryChange}
        activeView={viewId}
        onViewChange={handleViewChange}
        mode={mode}
        onModeChange={(nextMode) => {
          setSavedMode(nextMode);
          updateParams({ mode: nextMode });
        }}
        density={density}
        onDensityChange={(next) =>
          display.set('density', next === 'compact' ? 'compact' : 'comfortable')
        }
        grouping={grouping}
        onGroupingChange={(nextGrouping) => updateParams({ group: nextGrouping })}
        singleType={singleType}
        search={searchDraft}
        onSearchChange={setSearchDraft}
        workstreams={workstreams}
        members={members}
        typeCounts={typeCounts}
      />

      <div className={isWideView ? 'min-h-0 flex-1' : undefined}>
        {items.isPending ? (
          <TableSkeleton rows={8} columns={6} />
        ) : items.isError ? (
          <ErrorState error={items.error} onRetry={() => void items.refetch()} />
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<InboxIcon />}
            title={query.search ? 'Ничего не найдено' : 'Здесь пока пусто'}
            description={
              query.search
                ? 'По запросу ничего не найдено. Попробуйте изменить формулировку или сбросить фильтры.'
                : emptyDescription(query)
            }
            action={
              canCreate && !query.search ? (
                <Button onClick={() => setQuickAddOpen(true)}>
                  <PlusIcon />
                  Добавить элемент
                </Button>
              ) : null
            }
            secondaryAction={
              query.search || activeView.id !== 'all' ? (
                <Button variant="outline" onClick={() => handleViewChange('all')}>
                  Сбросить фильтры
                </Button>
              ) : null
            }
            hint={
              query.search ? null : (
                <>
                  Пятнадцать типов на любой случай: заметка, факт, вопрос, задача, решение, риск,
                  блокер, актив, расход. Быстрое добавление требует только тип и название —
                  остальное дописывается позже.
                </>
              )
            }
          />
        ) : mode === 'board' ? (
          <ItemsBoard
            projectId={project.id}
            items={rows}
            grouping={grouping}
            singleType={singleType}
            workstreams={workstreams}
            members={members}
            canEdit={canCreate}
            onOpenItem={openItem}
          />
        ) : mode === 'calendar' ? (
          <ItemsCalendar
            projectId={project.id}
            items={rows}
            canEdit={canCreate}
            onOpenItem={openItem}
          />
        ) : (
          <>
            {mode === 'list' ? (
              <ItemsList items={rows} selectedId={selectedId} onSelect={openItem} />
            ) : (
              <ItemsTable
                items={rows}
                selectedId={selectedId}
                onSelect={openItem}
                sort={query.sort ?? 'updatedAt'}
                direction={query.direction ?? 'desc'}
                onSortChange={handleSortChange}
                density={density}
              />
            )}

            {items.hasNextPage ? (
              <div className="flex justify-center py-3">
                <Button
                  variant="outline"
                  size="sm"
                  disabled={items.isFetchingNextPage}
                  onClick={() => void items.fetchNextPage()}
                >
                  {items.isFetchingNextPage ? <Loader2Icon className="animate-spin" /> : null}
                  Показать ещё
                </Button>
              </div>
            ) : (
              <p className="px-6 py-3 text-xs text-muted-foreground">
                Показаны все {pluralRu(totalCount, ['элемент', 'элемента', 'элементов'])}
              </p>
            )}
          </>
        )}
      </div>

      <ItemDetailPanel
        projectId={project.id}
        itemId={selectedId}
        onClose={() => updateParams({ item: null })}
        onOpenItem={openItem}
        canEdit={canCreate}
        canReview={project.permissions.includes('REVIEW_WRITE')}
        workstreams={workstreams}
        members={members.map((member) => member.user)}
      />

      <QuickAddDialog
        open={quickAddOpen}
        onOpenChange={setQuickAddOpen}
        projectId={project.id}
        onCreated={openItem}
      />
    </div>
  );
}

/** Пустое состояние объясняет, чего именно нет при текущих фильтрах. */
function emptyDescription(query: ItemQuery): string {
  if (query.isNextAction) {
    return 'Ни один элемент не отмечен как следующее действие. Отметьте задачу, исследование, вопрос или блокер — они появятся здесь и на обзоре проекта.';
  }
  if (query.type?.length === 1) {
    return 'Элементов этого типа пока нет. Добавьте первый — он появится в списке сразу.';
  }
  if (query.type?.length || query.status?.length || query.ownerId?.length) {
    return 'Под выбранные фильтры ничего не подошло. Снимите часть условий, чтобы увидеть больше.';
  }
  return 'Здесь собирается вся проектная информация: знания, работа, результаты, ресурсы и расходы.';
}
